import { observable, runInAction } from 'mobx';
import _ from 'lodash';
import { postJSON, setToken } from '../utils/Request';
import type { Login } from '../types/api';
import type { WechatQrLogin } from '../types/wechat';
import { readUserRegion } from '../utils/region';
import { resetPassword as resetPasswordRequest } from './request';

export default class LoginStore {
  data = observable.object<{ hasLogin: boolean; userinfo?: Login }>({
    hasLogin: false
  });

  constructor() {
    const user = this.readStoredUser();
    if (user && !_.isEmpty(user)) {
      runInAction(() => {
        this.data.hasLogin = true;
        this.data.userinfo = user;
      });
      setToken(user.token);
    }
  }

  private readStoredUser(): Login | null {
    const raw = localStorage.getItem('user');
    if (!raw || raw === 'undefined' || raw === 'null') {
      return null;
    }
    try {
      const parsed = JSON.parse(raw) as Login;
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
      localStorage.removeItem('user');
      return null;
    }
  }

  async register(params: {
    identity: string;
    code: string;
    password: string;
    displayName: string;
  }) {
    return await postJSON({
      path: '/api/v1/auth/register',
      params
    });
  }

  async sendVerificationCode(
    identity: string,
    scene: 'REGISTER' | 'LOGIN' | 'RESET_PASSWORD' | 'BINDING'
  ) {
    return await postJSON({
      path: '/api/v1/auth/verification-code',
      params: { identity, scene }
    });
  }

  async resetPassword(params: { identity: string; code: string; newPassword: string }) {
    return await resetPasswordRequest(params);
  }


  async logout() {
    localStorage.removeItem('expire');
    localStorage.removeItem('user');
    setToken(null);
    this.data.hasLogin = false;
    this.data.userinfo = undefined;
  }

  async loginByCode(params: { identity: string; code: string }) {
    return await postJSON({
      path: '/api/v1/auth/login/code',
      params
    });
  }

  async loginByPassword(params: { identity: string; password: string }) {
    return await postJSON({
      path: '/api/v1/auth/login',
      params
    });
  }

  /** 生成服务号登录二维码。不传请求体。返回体原样交给页面。 */
  async createWechatQrLogin() {
    return await postJSON<WechatQrLogin>({
      path: '/api/v1/auth/wechat/qrcode'
    });
  }

  applyLoginContent(content: unknown, identity: { phone: string } | { email: string }) {
    const session = toLoginSession(content, identity);
    if (!session) return null;
    runInAction(() => {
      this.data.hasLogin = true;
      this.data.userinfo = session;
    });
    localStorage.setItem('user', JSON.stringify(session));
    setToken(session.token);
    return session;
  }
}

function textOf(record: Record<string, unknown>, keys: string[]) {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return '';
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return null;
}

function toLoginSession(content: unknown, identity: { phone: string } | { email: string }): Login | null {
  const root = asRecord(content) ?? {};
  const profile = asRecord(root.user) ?? asRecord(root.userinfo) ?? root;
  const token =
    (typeof content === 'string' ? content.trim() : '') ||
    textOf(root, ['token', 'accessToken', 'access_token']) ||
    textOf(profile, ['token', 'accessToken', 'access_token']);
  if (!token) return null;
  const phone = 'phone' in identity ? identity.phone : '';
  const email = 'email' in identity ? identity.email : '';
  const region = textOf(profile, ['region']) || readUserRegion() || undefined;
  return {
    userUid: textOf(profile, ['userUid', 'uid', 'id']),
    token,
    userName: textOf(profile, ['userName', 'displayName', 'name']) || phone || email,
    avatar: textOf(profile, ['avatar']),
    backgroundImage: textOf(profile, ['backgroundImage']),
    mobile: textOf(profile, ['mobile', 'phone']) || phone,
    email: textOf(profile, ['email']) || email,
    userType: typeof profile.userType === 'number' ? profile.userType : 0,
    region
  };
}
